import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';

import popimg from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface ELAddDNCNumbersProps {
    mode?: 'ADD' | 'SEARCH';
    onClose?: () => void;
}

interface DropdownOption {
    label: string;
    value: string;
}

const styles = {
    body: {
        margin: '0px',
        fontFamily: 'Trebuchet MS, Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'Azure',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
    },
    container: {
        padding: '0px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        overflow: 'hidden',
    },
    headerBar: {
        backgroundImage: `url(${popimg}), url(${popbg})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "left center, right center",
        backgroundSize: "30% 100%, 70% 100%",
        height: "16px",
        padding: "7px 0px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: '100%'
    },
    titleBar: {
        color: 'white',
        fontSize: '20px',
        fontWeight: 'bold',
        background: '#008080',
        marginBottom: '20px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    uploadBox: {
        width: '100%',
        maxWidth: '800px',
        textAlign: 'center' as const,
        marginTop: '0px',
    },
    searchBox: {
        width: '100%',
        maxWidth: '800px',
        textAlign: 'center' as const,
        marginTop: '0px',
    },
    inputStyle: {
        width: '120px',
        borderColor: '#5B91B5',
        borderStyle: 'solid',
        borderWidth: '1px',
        fontSize: '14px',
        fontFamily: 'Arial'
    }
};

function EL_AddDNCNumbers({ mode, onClose }: ELAddDNCNumbersProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    
    const pathName = location.pathname;
    const isSearchMode = mode === 'SEARCH' || (pathName.includes('EL_AddDNCNumbers') && !pathName.includes('EL_AddDNCNumbers_New') && searchParams.get('DNC')?.toUpperCase() === 'SEARCH');
    const isAddMode = mode === 'ADD' || (pathName.includes('EL_AddDNCNumbers_New') && searchParams.get('DNCAction')?.toUpperCase() === 'ADD');

    const [categories, setCategories] = useState<DropdownOption[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [category, setCategory] = useState('');
    const [areaCode, setAreaCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    useEffect(() => {
        if (isAddMode) {
            const fetchCategories = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}api/get_dnc_categories`);
                    const result = await response.json();
                    if (result.status === 'success') {
                        setCategories(result.categories);
                    } else {
                        setAlertInfo({ show: true, message: 'Failed to load categories.' });
                    }
                } catch (error) {
                    setAlertInfo({ show: true, message: 'Network error loading categories.' });
                }
            };
            fetchCategories();
        }
    }, [isAddMode]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
            
            if (fileExtension !== 'csv') {
                setAlertInfo({ show: true, message: 'Invalid file format <br /> Please select a csv file' });
                setFile(null);
                e.target.value = '';
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleUpload = async () => {
        if (!category) {
            setAlertInfo({ show: true, message: 'Please select a Category.' });
            return;
        }
        if (!file) {
            setAlertInfo({ show: true, message: 'Please select a file.' });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);

        try {
            const response = await fetch(`${API_BASE_URL}api/upload_dnc_list`, {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();

            if (result.status === 'success') {
                setAlertInfo({ show: true, message: result.message || 'Successfully Uploaded data' });
                setFile(null);
                setCategory('');
                const fileInput = document.getElementById('csvFileInput') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
            } else {
                setAlertInfo({ show: true, message: result.message || 'Failed to upload file.' });
            }
        } catch (error: any) {
            setAlertInfo({ show: true, message: 'Network Error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!areaCode.trim() || !phoneNumber.trim()) {
            setAlertInfo({ show: true, message: 'Please enter both fields to search' });
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                areaCode: areaCode.trim(),
                phoneNumber: phoneNumber.trim()
            });

            const response = await fetch(`${API_BASE_URL}api/search_dnc_number?${queryParams}`);
            const result = await response.json();

            if (result.status === 'success') {
                if (result.exists) {
                    setAlertInfo({ 
                        show: true, 
                        message: `Number exists in<br /><br /><b>${result.list_name}</b>` 
                    });
                } else {
                    setAlertInfo({ 
                        show: true, 
                        message: 'Number does not exist in any of our lists' 
                    });
                }
            } else {
                setAlertInfo({ show: true, message: result.message || 'Search failed.' });
            }
        } catch (error: any) {
            setAlertInfo({ show: true, message: 'Network Error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/SysAdmin');
        }
    };

    const handleBackToMenu = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/SysAdmin');
        }
    };

    return (
        <div style={styles.body}>
            <div style={styles.container}>
                {isAddMode && (
                    <div style={styles.uploadBox}>
                        <div style={styles.headerBar}>
                            <img
                                src={popclose}
                                alt="close"
                                height="30px"
                                style={{ cursor: "pointer" }}
                                onClick={handleClose}
                            />
                        </div>

                        <div style={{ ...styles.titleBar, height: '40px' }}>Import DNC List</div>
                        
                        <table cellPadding={5} cellSpacing={0} style={{ width: '100%', border: '0' }}>
                            <tbody>
                                <tr>
                                    <td style={{ textAlign: 'right', width: '50%', fontSize: '18px', verticalAlign: 'middle', color: 'black' }}>
                                        Category :
                                    </td>
                                    <td style={{ textAlign: 'left' }}>
                                        <select 
                                            value={category} 
                                            onChange={(e) => setCategory(e.target.value)}
                                            style={styles.inputStyle}
                                        >
                                            <option value="">--- Select ---</option>
                                            {categories.map((cat, index) => (
                                                <option key={index} value={cat.value}>
                                                    {cat.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'right', width: '50%', fontSize: '18px', verticalAlign: 'middle', color: 'black' }}>
                                        Select *.csv file to import :
                                    </td>
                                    <td style={{ textAlign: 'left' }}>
                                        <div style={{ ...styles.inputStyle, width: '250px', display: 'inline-flex', alignItems: 'center', padding: '1px', backgroundColor: 'white' }}>

                                            <input 
                                                id="csvFileInput"
                                                type="file" 
                                                accept=".csv"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                            />

                                            <label 
                                                htmlFor="csvFileInput"
                                                style={{
                                                    backgroundColor: '#EFEFEF',
                                                    border: '1px solid #767676',
                                                    padding: '1px 6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    color: 'black',
                                                    marginRight: '5px',
                                                    marginBottom: '0px'
                                                }}
                                            >
                                                Choose File
                                            </label>
                                            
                                            <span style={{ fontSize: '13px', color: 'black', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                {file ? file.name : 'No file chosen'}
                                            </span>

                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'center', paddingTop: '20px' }}>
                                        <button
                                            style={{ width: '100px', fontSize: '14px', fontFamily: 'Arial', cursor: 'pointer', padding: '4px', marginRight: '20px' }}
                                            onClick={handleBackToMenu}
                                            disabled={loading}
                                        >
                                            Close
                                        </button>
                                        <button
                                            style={{ width: '100px', fontSize: '14px', fontFamily: 'Arial', cursor: 'pointer', padding: '4px' }}
                                            onClick={handleUpload}
                                        >
                                            OK
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {isSearchMode && (
                    <div style={styles.searchBox}>
                        <div style={styles.headerBar}>
                            <img
                                src={popclose}
                                alt="close"
                                height="30px"
                                style={{ cursor: "pointer", marginRight: "0px" }}
                                onClick={handleClose}
                            />
                        </div>

                        <div style={{ ...styles.titleBar, marginTop: '30px', marginBottom: '20px', color: '#5B91B5', background: 'none' }}>
                            Search for DNC Numbers
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{color:'black'}}>Area Code:</span>
                            <input
                                type="text"
                                style={{fontFamily: 'Arial', fontSize: '14px', border: 'solid 1px #5B91B5', padding: '2px', width: '60px'}}
                                maxLength={3}
                                value={areaCode}
                                onChange={(e) => setAreaCode(e.target.value)}
                            />
                            
                            <span style={{ marginLeft: '10px', color:'black' }}>Phone Number:</span>
                            <input
                                type="text"
                                style={{fontFamily: 'Arial', fontSize: '14px', border: 'solid 1px #5B91B5', padding: '2px', width: '100px'}}
                                maxLength={7}
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                            />
                            
                            <button style={{fontSize: '14px', padding: '2px 15px', cursor: 'pointer', margin: '0 5px'}} onClick={handleSearch} disabled={loading}>
                                Search
                            </button>
                            
                            <button style={{fontSize: '14px', padding: '2px 15px', cursor: 'pointer', margin: '0 5px'}} onClick={handleClose}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {loading && <Loading />}

            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
}

export default EL_AddDNCNumbers;